import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Switch, FormControlLabel,
  TextField, Alert, Snackbar, CircularProgress, Divider
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import axios from '../../utils/CustomAxios';
import { useDispatch } from 'react-redux';
import { updatepageName } from '../../Redux/page';
import {
  GrowthPrimaryButton,
  GrowthSecondaryButton,
} from '../growth_hub/GrowthModal';

export function SubscriptionSettings() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [settings, setSettings] = useState({
    subscriptions_enabled: true,
    premium_enabled: true,
    premium_upgrade_threshold: 500,
    multiple_inventory_subscriptions: true,
    max_subscriptions_per_user: 5,
    mobile_subscription_enabled: true,
    show_subscription_banner: true
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    dispatch(updatepageName("subscription_settings"));
    fetchSettings();
  }, [dispatch]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('subscriptions/config/');
      if (response.data && Object.keys(response.data).length > 0) {
        setSettings({
          subscriptions_enabled: response.data.subscriptions_enabled ?? true,
          premium_enabled: response.data.premium_enabled ?? true,
          premium_upgrade_threshold: response.data.premium_upgrade_threshold ?? 500,
          multiple_inventory_subscriptions: response.data.multiple_inventory_subscriptions ?? true,
          max_subscriptions_per_user: response.data.max_subscriptions_per_user ?? 5,
          mobile_subscription_enabled: response.data.mobile_subscription_enabled ?? true,
          show_subscription_banner: response.data.show_subscription_banner ?? true
        });
      } else {
        // No settings found, show message to create configuration
        showSnackbar('No subscription configuration found. Please save settings to create one.', 'warning');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Settings don't exist yet, use defaults
        showSnackbar('No subscription configuration exists. Using default settings.', 'info');
      } else {
        showSnackbar('Failed to load subscription settings. Please check your connection.', 'error');
      }
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateSettings = () => {
    const errors = {};

    if (settings.premium_upgrade_threshold < 0) {
      errors.premium_upgrade_threshold = 'Threshold cannot be negative';
    }

    if (settings.max_subscriptions_per_user < 0) {
      errors.max_subscriptions_per_user = 'Maximum subscriptions cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      showSnackbar('Please fix the validation errors', 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put('subscriptions/config/', settings);
      showSnackbar('Subscription settings saved successfully');
      setSettings(response.data);
      setValidationErrors({});
    } catch (error) {
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          'Failed to save subscription settings';
      showSnackbar(errorMessage, 'error');
    }
    setSaving(false);
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Subscription Settings
        </Typography>
        <GrowthSecondaryButton
          startIcon={<Refresh />}
          onClick={fetchSettings}
        >
          Refresh
        </GrowthSecondaryButton>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These settings apply to your entire franchise. Changes will affect all users and hubs.
      </Alert>

      <Grid container spacing={3}>
        {/* Core Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Core Subscription Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.subscriptions_enabled}
                        onChange={(e) => handleInputChange('subscriptions_enabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Subscriptions"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Allow users to purchase and use subscriptions
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.premium_enabled}
                        onChange={(e) => handleInputChange('premium_enabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Premium Features"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Enable premium subscription benefits and features
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Premium Upgrade Threshold (₹)"
                    type="number"
                    value={settings.premium_upgrade_threshold}
                    onChange={(e) => handleInputChange('premium_upgrade_threshold', parseFloat(e.target.value) || 0)}
                    helperText="Minimum order amount to show premium upgrade prompts"
                    error={!!validationErrors.premium_upgrade_threshold}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  {validationErrors.premium_upgrade_threshold && (
                    <Typography variant="caption" color="error">
                      {validationErrors.premium_upgrade_threshold}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Limits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Subscription Limits
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.multiple_inventory_subscriptions}
                        onChange={(e) => handleInputChange('multiple_inventory_subscriptions', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Allow Multiple Hub Subscriptions"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Users can subscribe to multiple hubs simultaneously
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Max Subscriptions Per User"
                    type="number"
                    value={settings.max_subscriptions_per_user}
                    onChange={(e) => handleInputChange('max_subscriptions_per_user', parseInt(e.target.value) || 0)}
                    helperText="0 = unlimited subscriptions"
                    disabled={!settings.multiple_inventory_subscriptions}
                    error={!!validationErrors.max_subscriptions_per_user}
                    inputProps={{ min: 0 }}
                  />
                  {validationErrors.max_subscriptions_per_user && (
                    <Typography variant="caption" color="error">
                      {validationErrors.max_subscriptions_per_user}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Mobile App Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Mobile App Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.mobile_subscription_enabled}
                        onChange={(e) => handleInputChange('mobile_subscription_enabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Mobile Subscriptions"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Allow mobile app users to purchase subscriptions
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.show_subscription_banner}
                        onChange={(e) => handleInputChange('show_subscription_banner', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Show Subscription Banner"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Display subscription promotion banners in mobile app
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Summary */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Settings Summary
              </Typography>
              <Typography variant="body2">
                <strong>Subscriptions:</strong> {settings.subscriptions_enabled ? 'Enabled' : 'Disabled'}<br/>
                <strong>Premium Features:</strong> {settings.premium_enabled ? 'Enabled' : 'Disabled'}<br/>
                <strong>Upgrade Threshold:</strong> ₹{settings.premium_upgrade_threshold}<br/>
                <strong>Multiple Hubs:</strong> {settings.multiple_inventory_subscriptions ? 'Allowed' : 'Not Allowed'}<br/>
                <strong>Max Per User:</strong> {settings.max_subscriptions_per_user || 'Unlimited'}<br/>
                <strong>Mobile App:</strong> {settings.mobile_subscription_enabled ? 'Enabled' : 'Disabled'}<br/>
                <strong>Banners:</strong> {settings.show_subscription_banner ? 'Shown' : 'Hidden'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" mt={2}>
            <GrowthPrimaryButton
              size="large"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 200, py: 1.5 }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </GrowthPrimaryButton>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
